import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from "../../context/AuthContext";

const navItems = [
  {
    section: "Main",
    items: [
      { to: "/dashboard", icon: <i className="lni lni-dashboard-square-1 icon-md"></i>, label: "Dashboard" },
    ],
  },
  {
    section: "Campus Setup",
    items: [
      { to: "/branches", icon: <i className="bi bi-bank icon-sm"></i>, label: "Institution Management" },
      { to: "/admin", icon: <i class="lni lni-target-user icon-md"></i>, label: "Administration" },
    ],
  },
  {
    section: "Admissions CRM",
    items: [
      { to: "/inquiry", icon: <i className="lni lni-search-text icon-md"></i>, label: "Inquiry Management" },
    ],
  },
  {
    section: "Academic",
    items: [
      { to: "/academic", icon: <i className="lni lni-book-1 icon-md"></i>, label: "Academic" },
      { to: "/classes", icon: <i className="lni lni-buildings-1 icon-md"></i>, label: "Classes & Sections" },
      { to: "/students", icon: <i className="lni lni-graduation-cap-1 icon-md"></i>, label: "Students" },
      { to: "/staff", icon: <i className="lni lni-user-multiple-4 icon-md"></i>, label: "Staff" },
      { to: "/departments", icon: <i className="lni lni-layout-9 icon-md"></i>, label: "Departments" },
      { to: "/attendance", icon: <i className="lni lni-check-square-2 icon-md"></i>, label: "Attendance" },
      { to: "/examination", icon: <i className="lni lni-file-pencil icon-md"></i>, label: "Examination" },
      { to: "/grades", icon: <i className="lni lni-spellcheck icon-md"></i>, label: "Grade Management" },
      { to: "/id-cards-config", icon: <i className="bi bi-person-vcard icon-sm"></i>, label: "ID Cards & Certificate Config" },
    ],
  },
  {
    section: "Finance",
    items: [
      { to: "/fees", icon: <i className="lni lni-wallet-1 icon-md"></i>, label: "Fee Management" },
    ],
  },
  {
    section: "Human Resources",
    items: [
      { to: "/hr", icon: <i className="lni lni-hand-taking-user icon-md"></i>, label: "HR Management" },
    ],
  },
  {
    section: "Campus",
    items: [
      { to: "/transport", icon: <i className="lni lni-bus-1 icon-md"></i>, label: "Transport" },
      { to: "/library", icon: <i className="lni lni-books-2 icon-md"></i>, label: "Library" },
      { to: "/hostel", icon: <i className="lni lni-burger-drink icon-md"></i>, label: "Hostel" },
      { to: "/inventory", icon: <i className="lni lni-box-closed icon-md"></i>, label: "Inventory" },
    ],
  },
  {
    section: "Communication",
    items: [
      { to: "/communication", icon: <i className="lni lni-tidio-ai icon-md"></i>, label: "Communication" },
      { to: "/tickets", icon: <i className="lni lni-sports icon-md"></i>, label: "Support Tickets" },
    ],
  },
  {
    section: "System",
    items: [
      { to: "/reports", icon: <i className="lni lni-trend-up-1 icon-md"></i>, label: "Reports & Analytics" },
    ],
  },
];

// Map menu routes to permission module names
const moduleMap = {
  "/dashboard": "Dashboard",
  "/branches": "Institution",
  "/admin": "Permissions",
  "/admin": "Administration",

  "/academic": "Academic Year",
  "/classes": "Classes",
  "/students": "Students",
  "/departments": "Departments",
  "/staff": "Staff",
  "/attendance": "Attendance",
  "/examination": "Examinations",
  "/grades": "Results",
  "/id-cards-config": "Certificates",
  "/fees": "Fee Management",
  "/hr": "Payroll",
  "/inquiry": "Visitors",
  "/transport": "Transport",
  "/library": "Library",
  "/hostel": "Hostel",
  "/inventory": "Inventory",
  "/communication": "SMS",
  "/tickets": "Notifications",
  "/reports": "Reports",
};

const EXPANDED_WIDTH = 260;
const COLLAPSED_WIDTH = 72;

const Sidebar = ({ collapsed, mobileOpen, isMobile, onClose }) => {
  const { hasPermission } = useAuth();
  const [hovered, setHovered] = useState(null); // { label, top }

  const showIconsOnly = collapsed && !isMobile;
  const width = isMobile ? EXPANDED_WIDTH : (collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH);

  const sidebarStyle = {
    width,
    minWidth: width,
    flex: `0 0 ${width}px`,
    overflow: 'hidden',
    transition: 'width 0.25s ease, flex-basis 0.25s ease, transform 0.25s ease',
    ...(isMobile
      ? {
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100vh',
        zIndex: 1040,
        transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
      }
      : { position: 'relative' }),
  };

  const handleEnter = (e, label) => {
    if (!showIconsOnly) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setHovered({ label, top: rect.top + rect.height / 2 });
  };

  const handleLeave = () => setHovered(null);

  return (
    <div className="sidebar" style={sidebarStyle}>
      <NavLink to="/dashboard" className="sidebar-brand" onClick={onClose}>
        <div className="brand-icon">S</div>
        {!showIconsOnly && (
          <div className="brand-text">
            <h5>SchoolERP</h5>
            <small>Management System</small>
          </div>
        )}
      </NavLink>

      <nav className="sidebar-nav">
        {navItems.map((section) => {
          const visibleItems = section.items.filter((item) => {
            const moduleName = moduleMap[item.to];
            if (!moduleName) return true;
            return hasPermission(moduleName, "can_view");
          });

          if (visibleItems.length === 0) return null;

          return (
            <div key={section.section}>
              {!showIconsOnly && (
                <div className="nav-section-title">
                  {section.section}
                </div>
              )}

              <ul className="nav flex-column">
                {visibleItems.map((item) => (
                  <li className="nav-item" key={item.to}>
                    <NavLink
                      to={item.to}
                      className={({ isActive }) =>
                        `nav-link ${isActive ? "active" : ""}`
                      }
                      style={
                        showIconsOnly
                          ? {
                            justifyContent: "center",
                            paddingLeft: 0,
                            paddingRight: 0,
                          }
                          : undefined
                      }
                      onClick={onClose}
                      onMouseEnter={(e) => handleEnter(e, item.label)}
                      onMouseLeave={handleLeave}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      {!showIconsOnly && <span>{item.label}</span>}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>

      {!showIconsOnly && (
        <div className="sidebar-footer">
          <div>© 2026 SmartEdus. All Rights Reserved.</div>
          <div>Developed by: CodexCore.in</div>
        </div>
      )}

      {showIconsOnly && hovered && (
        <div
          style={{
            position: 'fixed',
            left: COLLAPSED_WIDTH + 10,
            top: hovered.top,
            transform: 'translateY(-50%)',
            background: '#1e293b',
            color: '#fff',
            padding: '6px 14px',
            borderRadius: 6,
            fontSize: 13,
            whiteSpace: 'nowrap',
            zIndex: 2000,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            pointerEvents: 'none',
          }}
        >
          {hovered.label}
        </div>
      )}
    </div>
  );
};

export default Sidebar; 